import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import {
  api,
  type ChapterQuestions,
  type MemberProfile,
  type Meeting,
  type Moment,
  type Rsvp,
} from "../api";
import { useAuth } from "../auth";
import { Avatar } from "../components/Avatar";
import { Button, Card, SectionHeading, Spinner } from "../components/ui";

// In-meeting "room" view — the live conversation companion. Designed for a
// host running the meeting from a laptop/tablet, surfacing questions,
// rotating speakers, and capturing memorable moments.

export default function MeetingRoomPage() {
  const { id } = useParams<{ id: string }>();
  const meetingId = Number(id);
  const qc = useQueryClient();
  const { user } = useAuth();

  const meetings = useQuery({
    queryKey: ["meetings"],
    queryFn: () => api.get<{ meetings: Meeting[] }>("/meetings"),
    enabled: !!meetingId,
  });

  const meeting = meetings.data?.meetings.find((m) => m.id === meetingId) ?? null;
  const bookId = meeting?.bookId ?? null;

  const questions = useQuery({
    queryKey: ["ai", bookId ?? 0, "questions"],
    queryFn: () =>
      api.get<{ content: { type: string; data: ChapterQuestions[] } | null }>(
        `/ai/${bookId}/questions`
      ),
    enabled: !!bookId,
    select: (r) => r.content?.data ?? [],
  });

  const rsvps = useQuery({
    queryKey: ["rsvps", meetingId],
    queryFn: () => api.get<{ rsvps: Rsvp[] }>(`/meetings/${meetingId}/rsvps`),
    enabled: !!meetingId,
  });

  const members = useQuery({
    queryKey: ["members"],
    queryFn: () => api.get<{ members: MemberProfile[] }>("/members"),
  });

  const moments = useQuery({
    queryKey: ["moments", meetingId],
    queryFn: () => api.get<{ moments: Moment[] }>(`/moments/${meetingId}`),
    enabled: !!meetingId,
    refetchInterval: 8_000,
  });

  // Flatten all questions across chapters with a chapter ref.
  const allQuestions = useMemo(() => {
    const list: { chapter: number; title: string | null; q: string }[] = [];
    for (const ch of questions.data ?? []) {
      for (const q of ch.questions) {
        list.push({ chapter: ch.number, title: ch.title, q });
      }
    }
    return list;
  }, [questions.data]);

  const [qIdx, setQIdx] = useState(0);

  // Timer — persisted in localStorage per meeting so closing/reopening
  // doesn't reset.
  const timerKey = `meeting-room-started-${meetingId}`;
  const [startedAt, setStartedAt] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const v = localStorage.getItem(timerKey);
    return v ? Number(v) : null;
  });
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, [startedAt]);

  function startMeeting() {
    const t = Date.now();
    setStartedAt(t);
    localStorage.setItem(timerKey, String(t));
  }
  function resetTimer() {
    setStartedAt(null);
    localStorage.removeItem(timerKey);
  }

  // Speaker rotation — track who's spoken in this session (in-memory).
  // Members with low "spoken count" bubble to the top of "haven't heard from."
  const [spokenCount, setSpokenCount] = useState<Record<number, number>>({});

  function spotlight(userId: number) {
    setSpokenCount((s) => ({ ...s, [userId]: (s[userId] ?? 0) + 1 }));
  }

  // Moment composer
  const [momentBody, setMomentBody] = useState("");
  const [attributedTo, setAttributedTo] = useState<number | null>(null);
  const captureRef = useRef<HTMLTextAreaElement>(null);

  const captureMoment = useMutation({
    mutationFn: () =>
      api.post(`/moments/${meetingId}`, {
        body: momentBody.trim(),
        attributedToUserId: attributedTo,
      }),
    onSuccess: () => {
      setMomentBody("");
      setAttributedTo(null);
      qc.invalidateQueries({ queryKey: ["moments", meetingId] });
    },
  });

  const deleteMoment = useMutation({
    mutationFn: (mid: number) => api.del(`/moments/${mid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["moments", meetingId] }),
  });

  if (meetings.isLoading) return <Spinner />;
  if (!meeting) return <p className="text-plum-soft">Meeting not found.</p>;

  const date = parseISO(meeting.meetsAt);
  const attendingIds = new Set(
    (rsvps.data?.rsvps ?? [])
      .filter((r) => r.status === "yes" || r.status === "maybe")
      .map((r) => r.userId)
  );
  const attendingMembers = (members.data?.members ?? []).filter((m) => attendingIds.has(m.id));
  // Sort by spoken count asc so quietest people surface first.
  const sortedSpeakers = [...attendingMembers].sort(
    (a, b) => (spokenCount[a.id] ?? 0) - (spokenCount[b.id] ?? 0)
  );

  const totalQs = allQuestions.length;
  const currentQ = totalQs > 0 ? allQuestions[Math.max(0, Math.min(qIdx, totalQs - 1))] : null;

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/calendar" className="text-sm text-plum-mute hover:text-rose">
          ← Calendar
        </Link>
        <Link
          to={`/meetings/${meetingId}/packet`}
          className="text-xs uppercase tracking-[0.16em] px-4 py-1.5 rounded-full bg-cream-100 text-plum-soft hover:bg-blush-soft hover:text-rose-dark transition-colors"
        >
          Open packet →
        </Link>
      </div>

      <SectionHeading
        eyebrow="In the room"
        title={meeting.bookTitle ?? "Book club meeting"}
        description={`${format(date, "EEEE, MMMM d 'at' h:mm a")}${
          meeting.venueName ? ` · ${meeting.venueName}` : ""
        }`}
      />

      {/* Timer */}
      <Card accent>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[0.7rem] uppercase tracking-[0.18em] text-gold-dark mb-1">
              Tonight's timer
            </div>
            <div className="font-serif text-4xl text-plum tabular-nums">
              {startedAt ? formatDuration(now - startedAt) : "—"}
            </div>
          </div>
          {!startedAt ? (
            <Button onClick={startMeeting}>Start meeting</Button>
          ) : (
            <button
              onClick={resetTimer}
              className="text-xs uppercase tracking-[0.14em] text-plum-mute hover:text-rose px-3 py-1.5"
            >
              Reset timer
            </button>
          )}
        </div>
      </Card>

      {/* Question navigator */}
      <Card accent>
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <span className="text-[0.7rem] uppercase tracking-[0.18em] text-gold-dark">
              Question {totalQs > 0 ? `${Math.min(qIdx + 1, totalQs)} of ${totalQs}` : ""}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <NavBtn label="←" onClick={() => setQIdx((i) => Math.max(0, i - 1))} disabled={qIdx === 0 || totalQs === 0} />
            <NavBtn
              label="Random"
              onClick={() => setQIdx(Math.floor(Math.random() * totalQs))}
              disabled={totalQs === 0}
            />
            <NavBtn
              label="→"
              onClick={() => setQIdx((i) => Math.min(totalQs - 1, i + 1))}
              disabled={qIdx >= totalQs - 1 || totalQs === 0}
            />
          </div>
        </div>
        {currentQ ? (
          <div>
            <p className="font-serif text-2xl text-plum leading-snug">{currentQ.q}</p>
            <p className="text-xs text-plum-mute mt-3 uppercase tracking-[0.14em]">
              Chapter {currentQ.chapter}
              {currentQ.title ? ` · ${currentQ.title}` : ""}
            </p>
          </div>
        ) : (
          <p className="text-plum-mute italic">
            No discussion questions cached yet — generate them from the book page first.
          </p>
        )}
      </Card>

      {/* Speaker rotation */}
      <Card>
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <div>
            <span className="text-[0.7rem] uppercase tracking-[0.18em] text-gold">
              Who's spoken
            </span>
            <h3 className="font-serif text-xl text-plum mt-0.5">
              Pass the mic
            </h3>
          </div>
          {Object.keys(spokenCount).length > 0 ? (
            <button
              onClick={() => setSpokenCount({})}
              className="text-xs uppercase tracking-[0.14em] text-plum-mute hover:text-rose"
            >
              Reset
            </button>
          ) : null}
        </div>
        {sortedSpeakers.length === 0 ? (
          <p className="text-plum-mute italic text-sm">
            No RSVPs yet. People appear here once they've said they're coming.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2.5">
            {sortedSpeakers.map((m) => {
              const count = spokenCount[m.id] ?? 0;
              return (
                <button
                  key={m.id}
                  onClick={() => spotlight(m.id)}
                  className={[
                    "flex items-center gap-2.5 px-3 py-2 rounded-full border transition-colors",
                    count === 0
                      ? "bg-blush-soft/40 border-rose-light/50 text-rose-dark"
                      : count === 1
                      ? "bg-cream-100 border-cream-300 text-plum"
                      : "bg-cream-50 border-cream-200 text-plum-mute opacity-70",
                  ].join(" ")}
                  title={count === 0 ? "Haven't heard from yet" : `Spoken ${count}×`}
                >
                  <Avatar name={m.name} email={m.email} hue={m.avatarHue} size="xs" />
                  <span className="text-sm">{m.name ?? m.email.split("@")[0]}</span>
                  {count > 0 ? (
                    <span className="text-[0.65rem] font-mono">×{count}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {/* Moment capture */}
      <Card accent>
        <div className="mb-4">
          <span className="text-[0.7rem] uppercase tracking-[0.18em] text-gold">
            Capture moments
          </span>
          <h3 className="font-serif text-xl text-plum mt-0.5">
            Something worth remembering
          </h3>
          <p className="text-sm text-plum-soft mt-1">
            A great line, a hot take, a surprise — write it down before it's gone. Saved with this meeting forever.
          </p>
        </div>
        <textarea
          ref={captureRef}
          value={momentBody}
          onChange={(e) => setMomentBody(e.target.value)}
          rows={2}
          placeholder="Sarah said Emily's diary felt like reading her own teenage shame…"
          className="w-full px-4 py-3 rounded-xl bg-cream-50 border border-cream-300 text-plum placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose resize-none"
        />
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <select
            value={attributedTo ?? ""}
            onChange={(e) => setAttributedTo(e.target.value ? Number(e.target.value) : null)}
            className="px-3 py-2 rounded-lg bg-cream-50 border border-cream-300 text-sm text-plum focus:outline-none focus:border-rose"
          >
            <option value="">Who said it? (optional)</option>
            {attendingMembers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name ?? m.email.split("@")[0]}
              </option>
            ))}
          </select>
          <button
            disabled={!momentBody.trim() || captureMoment.isPending}
            onClick={() => captureMoment.mutate()}
            className="ml-auto text-xs uppercase tracking-[0.14em] px-4 py-2 rounded-full bg-rose text-white hover:bg-rose-dark disabled:opacity-50"
          >
            {captureMoment.isPending ? "Saving…" : "Capture"}
          </button>
        </div>
      </Card>

      {/* Moments feed */}
      {(moments.data?.moments.length ?? 0) > 0 ? (
        <div className="flex flex-col gap-4">
          <h3 className="text-[0.7rem] uppercase tracking-[0.18em] text-gold-dark">
            Tonight's moments · {moments.data!.moments.length}
          </h3>
          <ul className="flex flex-col gap-3">
            {moments.data!.moments.map((m) => (
              <li key={m.id} className="flex gap-3">
                {m.attributedTo ? (
                  <Avatar name={m.attributedTo.name} email={m.attributedTo.email} size="sm" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-cream-200 shrink-0" />
                )}
                <div className="flex-1 px-4 py-3 rounded-xl bg-cream-50/60 border border-cream-200">
                  <p className="text-plum font-serif italic leading-relaxed">{m.body}</p>
                  <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap text-xs text-plum-mute">
                    <span>
                      {m.attributedTo ? (
                        <>— {m.attributedTo.name ?? m.attributedTo.email.split("@")[0]}</>
                      ) : (
                        <span className="italic">unattributed</span>
                      )}
                      {m.capturedBy ? (
                        <span className="ml-2">
                          (captured by {m.capturedBy.name ?? m.capturedBy.email.split("@")[0]})
                        </span>
                      ) : null}
                    </span>
                    {m.capturedBy?.id === user?.id ? (
                      <button
                        onClick={() => deleteMoment.mutate(m.id)}
                        className="uppercase tracking-[0.14em] hover:text-rose"
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function NavBtn({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-9 h-9 px-3 rounded-lg text-sm text-plum-soft hover:bg-cream-100 hover:text-plum disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      {label}
    </button>
  );
}

function formatDuration(ms: number): string {
  if (ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}
